import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash';
import { AvatarWithInfo } from '@src/components/AvatarWithInfo';
import { TextWithReadMore } from '@src/containers/TextWithReadMore';
import { ActorNameWithStatus } from '@src/components/ActorNameWithStatus';
import { getNameInitials } from '@src/util/getNameInitials';
import { relativeTime } from '@src/util/relativeTime';
import { Rating } from '@src/components/Rating';
import { RatingsList } from '@src/components/RatingsList';
import { AnonymousUserSvg } from '@src/components/AnonymousUserSvg';
import { UserCompanyAssociationTypeEnum, useReplyToReviewMutation } from '@src/graphql/generated';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { RatingColor } from '@src/components/Rating/enums/ratingColor.enum';
import { RatingSize } from '@src/components/Rating/enums/ratingSize.enum';
import { AvatarSize } from '@src/components/Avatar/enums/avatarSize.enum';
import { trackEvent } from '@src/metrics';
import { CreateReply } from '../CreateReply';
import { RepliedComment } from '../RepliedComment';
import './styles.scss';

export const CompanyReviewFeedItem = ({ review, wrapperClassName }) => {
  const [createReply, { loading: isLoading }] = useReplyToReviewMutation();

  const {
    id: reviewId,
    actor: { entity: actorEntity },
    createdAt,
    isAnonymous,
    positiveFeedback,
    negativeFeedback,
    title,
    ratings,
    ratings: { total: totalRating },
    userAssociation,
    reply,
  } = review;

  const { name, position, avatar, avatarColor } = actorEntity;

  const onPost = (message: string): void => {
    createReply({
      variables: {
        replyToReviewInput: {
          comment: message,
        },
        reviewId: reviewId,
      },
    });

    trackEvent({
      context: MetricContext.COMPANY_REVIEWS_VIEW,
      interaction: MetricAction.REPLY_REVIEW,
      payload: {
        'Review Id': reviewId,
      },
    });
  };

  return (
    <div className={classnames('review-feed-item', wrapperClassName)}>
      <div className="review-feed-item__header">
        <AvatarWithInfo
          avatarProps={{
            avatarColor: avatarColor,
            children: isAnonymous && <AnonymousUserSvg pathColor={avatarColor} />,
            initials: getNameInitials(name),
            size: AvatarSize.X_LARGE,
            source: !isAnonymous ? get(avatar, 'fullUrl') : undefined,
          }}
          name={
            <ActorNameWithStatus
              fullName={name}
              isAnonymous={isAnonymous}
              isWorking={userAssociation === UserCompanyAssociationTypeEnum.WORKING}
            />
          }
          subTitle={isAnonymous ? relativeTime(createdAt) : `${position} • ${relativeTime(createdAt)}`}
          wrapperClassName="review-feed-item__user-info"
        />
      </div>
      <div className="review-feed-item__body">
        <Rating
          activeColor={RatingColor.BLUE}
          isDisabled
          maxRating={5}
          result={totalRating}
          shouldDisplayOnlyActiveStars
          size={RatingSize.SMALL}
          wrapperClassName="review-feed-item__overall-rating"
        />

        {!title && !positiveFeedback && !negativeFeedback && (
          <div className="review-feed-item__user-ratings">
            <div className="review-feed-item__user-ratings-list">
              <RatingsList ratings={ratings} />
            </div>
            <h4 className="review-feed-item__no-written-review">No written review was added.</h4>
          </div>
        )}

        {title && <h3 className="review-feed-item__title">{title}</h3>}
        {positiveFeedback && (
          <div className="review-feed-item__positive-feedback">
            <TextWithReadMore lineLimit={2} text={`👍 ${positiveFeedback}`} textClassName="review-feed-item__comment" />
          </div>
        )}
        {negativeFeedback && (
          <TextWithReadMore lineLimit={2} text={`👎 ${negativeFeedback}`} textClassName="review-feed-item__comment" />
        )}
      </div>
      <div className="review-feed-item__footer">
        {reply ? (
          <RepliedComment reply={reply} />
        ) : (
          <CreateReply isLoading={isLoading} onPost={message => onPost(message)} />
        )}
      </div>
    </div>
  );
};

CompanyReviewFeedItem.propTypes = {
  onPost: PropTypes.func.isRequired,
  review: PropTypes.shape({
    actor: PropTypes.shape({
      entity: PropTypes.object.isRequired,
      type: PropTypes.string.isRequired,
    }).isRequired,
    createdAt: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    isAnonymous: PropTypes.bool.isRequired,
    negativeFeedback: PropTypes.string,
    positiveFeedback: PropTypes.string,
    ratings: PropTypes.object.isRequired,
    title: PropTypes.string,
    userAssociation: PropTypes.string.isRequired,
  }).isRequired,
  wrapperClassName: PropTypes.string,
};
